import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Alert, Button } from 'antd';
import { TableProps } from 'antd/lib/table';
import { QUERY_USER_FEE_LEDGER } from '../../queries/FeeLedgerQueries';
import FeeLedgerTable from './FeeLedgerTable';
import { TableScroll } from './types';

const defaultSort = { field: 'assessed_on', order: 'descend' as const };

export default function UserFeeLedgerTable({
  userId,
  active,
  scroll,
}: {
  userId: string;
  active: boolean;
  scroll?: TableScroll;
}) {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(100);
  const [sort, setSort] = useState<{
    field: string;
    order: 'ascend' | 'descend';
  }>(defaultSort);
  const { data, previousData, loading, error, refetch } = useQuery(
    QUERY_USER_FEE_LEDGER,
    {
      variables: {
        user_id: userId,
        page,
        size,
        order: {
          field: sort.field,
          order: sort.order === 'ascend' ? 'ASC' : 'DESC',
        },
      },
      skip: !active,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
    },
  );
  const ledger = data?.getFeeLedgerPageByUser;
  // Share one membership array with the existing historical-processor renderer.
  const rows = useMemo(
    () =>
      ledger?.list.map((record: any) => ({
        ...record,
        assignment: {
          ...record.assignment,
          user: { user_membership: ledger.memberships },
        },
      })) ?? [],
    [ledger],
  );

  const handleChange: TableProps<any>['onChange'] = (
    pagination,
    _filters,
    sorter,
    extra,
  ) => {
    const nextSize = pagination.pageSize ?? size;
    setSize(nextSize);
    setPage(
      extra.action === 'sort' || nextSize !== size
        ? 1
        : pagination.current ?? 1,
    );
    if (extra.action === 'sort') {
      const selected = Array.isArray(sorter) ? sorter[0] : sorter;
      setSort(
        selected.order && selected.columnKey
          ? { field: String(selected.columnKey), order: selected.order }
          : defaultSort,
      );
    }
  };

  return (
    <>
      {error && (
        <Alert
          type="error"
          showIcon
          message="Unable to load fee ledger"
          description={error.message}
          action={<Button onClick={() => refetch()}>Retry</Button>}
        />
      )}
      <FeeLedgerTable
        data={rows}
        isAssignmentIdShown
        loading={loading}
        serverSort={sort}
        onChange={handleChange}
        pagination={{
          current: page,
          pageSize: size,
          total:
            ledger?.total ?? previousData?.getFeeLedgerPageByUser?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100],
          showTotal: (total) => `Total ${total} items`,
        }}
        scroll={scroll}
      />
    </>
  );
}
